import React, { useState, useEffect, useCallback } from 'react';
import { 
  CalendarDays, 
  Search, 
  Filter, 
  Download, 
  CheckCircle, 
  XCircle, 
  Clock,
  User,
  Calendar,
  FileText,
  MessageSquare,
  Eye,
  X,
  Users,
  GraduationCap
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  serverTimestamp,
  getDocs 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const CoachingLeaveManagement = () => {
  const { currentUser, userProfile } = useRealtime();
  const [studentLeaveRequests, setStudentLeaveRequests] = useState([]);
  const [teacherLeaveRequests, setTeacherLeaveRequests] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // all, student, teacher
  const [batchFilter, setBatchFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [adminComments, setAdminComments] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  // Load batches for the coaching
  const loadBatches = useCallback(async () => {
    if (!userProfile?.coachingId) return;
    
    try {
      const q = query(
        collection(db, 'batches'),
        where('coachingId', '==', userProfile.coachingId)
      );
      const snapshot = await getDocs(q);
      const batchesList = [];
      snapshot.forEach((doc) => {
        batchesList.push({ id: doc.id, ...doc.data() });
      });
      setBatches(batchesList);
    } catch (error) {
      console.error('Error loading batches:', error);
    }
  }, [userProfile?.coachingId]);

  // Load student leave requests
  const loadStudentLeaveRequests = useCallback(() => {
    if (!userProfile?.coachingId || batches.length === 0) return;
    
    const batchIds = batches.map(batch => batch.id);
    
    const q = query(
      collection(db, 'leaveRequests'),
      where('batchId', 'in', batchIds.slice(0, 10)),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requestsList = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        const batch = batches.find(b => b.id === data.batchId);
        requestsList.push({ 
          id: doc.id, 
          ...data,
          batchName: batch?.name || 'Unknown Batch',
          requestType: 'student'
        });
      });
      setStudentLeaveRequests(requestsList);
    }, (error) => {
      console.error('Error loading student leave requests:', error);
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        const fallbackQ = query(
          collection(db, 'leaveRequests'),
          where('batchId', 'in', batchIds.slice(0, 10))
        );
        
        const fallbackUnsubscribe = onSnapshot(fallbackQ, (snapshot) => {
          const requestsList = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            const batch = batches.find(b => b.id === data.batchId);
            requestsList.push({ 
              id: doc.id, 
              ...data,
              batchName: batch?.name || 'Unknown Batch',
              requestType: 'student'
            });
          });
          requestsList.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return dateB.getTime() - dateA.getTime();
          });
          setStudentLeaveRequests(requestsList);
        });
        return fallbackUnsubscribe;
      }
    });
    
    return unsubscribe;
  }, [userProfile?.coachingId, batches]);

  // Load teacher leave requests
  const loadTeacherLeaveRequests = useCallback(() => {
    if (!userProfile?.coachingId) return;
    
    const q = query(
      collection(db, 'teacherLeaveRequests'),
      where('coachingId', '==', userProfile.coachingId),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requestsList = [];
      snapshot.forEach((doc) => {
        requestsList.push({ 
          id: doc.id, 
          ...doc.data(),
          requestType: 'teacher'
        });
      });
      setTeacherLeaveRequests(requestsList);
      setLoading(false);
    }, (error) => {
      console.error('Error loading teacher leave requests:', error);
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        const fallbackQ = query(
          collection(db, 'teacherLeaveRequests'),
          where('coachingId', '==', userProfile.coachingId)
        );
        
        const fallbackUnsubscribe = onSnapshot(fallbackQ, (snapshot) => {
          const requestsList = [];
          snapshot.forEach((doc) => {
            requestsList.push({ 
              id: doc.id, 
              ...doc.data(),
              requestType: 'teacher'
            });
          });
          requestsList.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return dateB.getTime() - dateA.getTime();
          });
          setTeacherLeaveRequests(requestsList);
          setLoading(false);
        });
        return fallbackUnsubscribe;
      } else {
        setLoading(false);
      }
    });
    
    return unsubscribe;
  }, [userProfile?.coachingId]);
  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    if (batches.length > 0) {
      const unsubscribeStudent = loadStudentLeaveRequests();
      return () => {
        if (unsubscribeStudent) unsubscribeStudent();
      };
    }
  }, [batches, loadStudentLeaveRequests]);

  useEffect(() => {
    const unsubscribeTeacher = loadTeacherLeaveRequests();
    return () => {
      if (unsubscribeTeacher) unsubscribeTeacher();
    };
  }, [loadTeacherLeaveRequests]);

  const handleUpdateLeaveStatus = async (requestId, newStatus, requestType) => {
    if (!adminComments.trim() && newStatus === 'rejected') {
      toast.error('Please provide comments for rejection');
      return;
    }

    // Validation checks
    if (!currentUser?.uid) {
      toast.error('Authentication error: Please log in again');
      return;
    }
    
    if (!userProfile) {
      toast.error('Profile not loaded: Please refresh the page');
      return;
    }
    
    if (!userProfile.coachingId) {
      toast.error('Coaching information not found in your profile. Please contact admin.');
      return;
    }

    if (userProfile.role !== 'coaching_admin' && userProfile.role !== 'admin' && userProfile.role !== 'superadmin') {
      toast.error('You do not have permission to update leave requests. Required role: coaching_admin');
      return;
    }

    // Check if the leave request belongs to this coaching
    if (selectedRequest && selectedRequest.coachingId !== userProfile.coachingId) {
      toast.error('This leave request does not belong to your coaching institute');
      return;
    }

    setUpdatingStatus(true);
    try {
      const collectionName = requestType === 'teacher' ? 'teacherLeaveRequests' : 'leaveRequests';
      const updateData = {
        status: newStatus,
        adminComments: adminComments.trim() || null,
        reviewedBy: currentUser.uid,
        reviewedByName: currentUser.displayName || currentUser.email || 'Admin',
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(doc(db, collectionName, requestId), updateData);
      
      toast.success(`Leave request ${newStatus} successfully`);
      setShowDetailsModal(false);
      setSelectedRequest(null);
      setAdminComments('');
    } catch (error) {
      // Provide specific error messages based on error type
      let errorMessage = 'Failed to update leave status';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied: You may not have access to update this leave request. Check your role and coaching assignment.';
      } else if (error.code === 'unauthenticated') {
        errorMessage = 'Authentication error: Please log in again.';
      } else if (error.code === 'not-found') {
        errorMessage = 'Leave request not found: It may have been deleted.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (error.message?.includes('network')) {
        errorMessage = 'Network error: Please check your internet connection.';
      }
      
      toast.error(errorMessage);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const calculateLeaveDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Combine and filter leave requests
  const allRequests = [...studentLeaveRequests, ...teacherLeaveRequests];
  const filteredRequests = allRequests.filter(request => {
    const name = request.requestType === 'teacher' ? request.teacherName : request.studentName;
    const matchesSearch = name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.batchName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesType = typeFilter === 'all' || request.requestType === typeFilter;
    const matchesBatch = batchFilter === 'all' || request.batchId === batchFilter;
    
    return matchesSearch && matchesStatus && matchesType && matchesBatch;
  });

  // Statistics
  const stats = {
    total: allRequests.length,
    pending: allRequests.filter(r => r.status === 'pending').length,
    approved: allRequests.filter(r => r.status === 'approved').length,
    rejected: allRequests.filter(r => r.status === 'rejected').length,
    students: studentLeaveRequests.length,
    teachers: teacherLeaveRequests.length
  };
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <CalendarDays className="w-8 h-8 mr-3 text-blue-600" />
            Leave Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage leave requests from students and teachers
          </p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/20">
                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/20">
                <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/20">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Approved</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.approved}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/20">
                <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Rejected</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.rejected}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/20">
                <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Students</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.students}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-indigo-100 dark:bg-indigo-900/20">
                <GraduationCap className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Teachers</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.teachers}</p>
              </div>
            </div>
          </div>
        </div>
        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, reason..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Types</option>
                <option value="student">Students</option>
                <option value="teacher">Teachers</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Batch
              </label>
              <select
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Batches</option>
                {batches.map(batch => (
                  <option key={batch.id} value={batch.id}>{batch.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setTypeFilter('all');
                  setBatchFilter('all');
                }}
                className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
        {/* Leave Requests Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Leave Requests ({filteredRequests.length})
            </h2>
          </div>

          {filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No leave requests found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Applicant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Leave Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredRequests.map((request) => (
                    <tr key={`${request.requestType}-${request.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                              request.requestType === 'teacher' 
                                ? 'bg-indigo-100 dark:bg-indigo-900/20' 
                                : 'bg-blue-100 dark:bg-blue-900/20'
                            }`}>
                              {request.requestType === 'teacher' ? (
                                <GraduationCap className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                              ) : (
                                <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                              )}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {request.requestType === 'teacher' ? request.teacherName : request.studentName}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {request.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          request.requestType === 'teacher' 
                            ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                          {request.requestType === 'teacher' ? (
                            <GraduationCap className="w-3 h-3 mr-1" />
                          ) : (
                            <User className="w-3 h-3 mr-1" />
                          )}
                          {request.requestType === 'teacher' ? 'Teacher' : 'Student'}
                        </span>
                        {request.batchName && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {request.batchName}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white font-medium">
                          {request.reason}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {request.leaveType} • {new Date(request.startDate).toLocaleDateString()} - {new Date(request.endDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {calculateLeaveDays(request.startDate, request.endDate)} day(s)
                          {request.halfDay && <span className="text-xs text-gray-500 block">Half Day</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                          {getStatusIcon(request.status)}
                          <span className="ml-1">{request.status.charAt(0).toUpperCase() + request.status.slice(1)}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setAdminComments(request.adminComments || '');
                            setShowDetailsModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* Details Modal */}
        {showDetailsModal && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {selectedRequest.requestType === 'teacher' ? 'Teacher' : 'Student'} Leave Request Details
                </h3>
                <button 
                  onClick={() => setShowDetailsModal(false)} 
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Applicant Info */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {selectedRequest.requestType === 'teacher' ? 'Teacher' : 'Student'} Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Name:</span>
                      <p className="text-gray-900 dark:text-white">
                        {selectedRequest.requestType === 'teacher' ? selectedRequest.teacherName : selectedRequest.studentName}
                      </p>
                    </div>
                    {selectedRequest.batchName && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Batch:</span>
                        <p className="text-gray-900 dark:text-white">{selectedRequest.batchName}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Leave Details */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Leave Details</h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span>
                      <p className="text-gray-900 dark:text-white">{selectedRequest.leaveType}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Reason:</span>
                      <p className="text-gray-900 dark:text-white">{selectedRequest.reason}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Start Date:</span>
                        <p className="text-gray-900 dark:text-white">{new Date(selectedRequest.startDate).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">End Date:</span>
                        <p className="text-gray-900 dark:text-white">{new Date(selectedRequest.endDate).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Duration:</span>
                      <p className="text-gray-900 dark:text-white">
                        {calculateLeaveDays(selectedRequest.startDate, selectedRequest.endDate)} day(s)
                        {selectedRequest.halfDay && ' (Half Day)'}
                      </p>
                    </div>
                    {selectedRequest.description && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Description:</span>
                        <p className="text-gray-900 dark:text-white">{selectedRequest.description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Current Status */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Current Status</h4>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedRequest.status)}`}>
                      {getStatusIcon(selectedRequest.status)}
                      <span className="ml-1">{selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}</span>
                    </span>
                    {selectedRequest.reviewedAt && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Reviewed on {selectedRequest.reviewedAt.toDate().toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {selectedRequest.adminComments && (
                    <div className="mt-3">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Previous Comments:</span>
                      <p className="text-gray-900 dark:text-white mt-1">{selectedRequest.adminComments}</p>
                    </div>
                  )}
                </div>

                {/* Admin Actions */}
                {selectedRequest.status === 'pending' && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Admin Action</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Comments (Optional for approval, Required for rejection)
                        </label>
                        <textarea
                          value={adminComments}
                          onChange={(e) => setAdminComments(e.target.value)}
                          placeholder="Add your comments here..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleUpdateLeaveStatus(selectedRequest.id, 'approved', selectedRequest.requestType)}
                          disabled={updatingStatus}
                          className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50 flex items-center justify-center"
                        >
                          {updatingStatus ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleUpdateLeaveStatus(selectedRequest.id, 'rejected', selectedRequest.requestType)}
                          disabled={updatingStatus}
                          className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50 flex items-center justify-center"
                        >
                          {updatingStatus ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 mr-2" />
                              Reject
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachingLeaveManagement;